/* eslint-disable no-unused-vars */
import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import Page, { PageModalProps } from 'components/Page';

import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { Form, Col, Radio, Input, Row, Tag, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

import DF from 'DisplayFramework';
import {
  getRefPayNettingView,
  getSIPayNettingView,
  getPAYPayNettingView,
  getWACCPayNettingView,
  getCIPayNettingView,
  PayNettingViewResponse,
  PAYListResponse,
} from 'services/PaymentServices';
import ParListSelect from 'components/ParListSelect';
import { toCurrency } from 'utils/utils';
import CIDetail from 'pages/ClientInvoice/CIDetail';
import SIDetail from 'pages/SupplierInvoice/SIDetail';
import ThirdPartyInput from 'components/ThirdPartyInput';
import NewButton from 'components/NewButton';
import FormInput from 'components/FormInput';
import { useModalState } from 'hooks/useModalState';
import { CIListResponse } from 'services/clientInvoice';
import { SIListResponse } from 'services/supplierInvoice';
import PayementModal from '../PayementModal';
import ClientInvoiceSelect from 'components/ClientInvoiceSelect';
import Text from 'antd/lib/typography/Text';
import ModalControllerNV from 'components/ModalControllerNV';
import SupplierInvoiceSelect from 'components/SupplierInvoiceSelect';
import PaymentSelect from 'components/PaymentSelect';
import { isNil } from 'lodash';
import ProposalModal from 'pages/ProposalV3/ProposalModal';

// eslint-disable-next-line no-unused-vars
const { Search } = Input;

export type NettingListProps = PageModalProps & {
  objectid?: any;
  objecttype?: string;
  hideFilters?: boolean;
  hideExtraAction?: boolean;
  visible?: boolean;
  showAsModal?: boolean;
  ismodal?: boolean;
  hideCol?: boolean;
  refetchNetting?: any;
};
const NettingList = ({
  objectid,
  objecttype,
  hideFilters,
  hideExtraAction,
  visible = false,
  onCancel,
  hideCol,
  showAsModal = false,
  ismodal,
  refetchNetting,
}: NettingListProps) => {
  const [form] = Form.useForm();
  // const lobjectid = objectid ?? 0;
  // const lobjecttype = objecttype ?? '';

  const onChangeCurrency = (value: any) => {
    setSearchParams({ ...searchParams, currcode: value });
  };

  const {
    close: closeClientInvoiceListModal,
    open: openClientInvoiceListModal,
    visible: visibleClientInvoiceListModal,
  } = useModalState();

  const {
    close: closeSupplierInvoiceListModal,
    open: openSupplierInvoiceListModal,
    visible: visibleSupplierInvoiceListModal,
  } = useModalState();

  const onClientInvoiceSelect = (selectedInvoice: CIListResponse) => {
    form.setFieldsValue({ objectid: selectedInvoice.cireference });

    searchParams.objectid = selectedInvoice.ciid;
    searchParams.objectreference = selectedInvoice.cireference;
    searchParams.objecttype = 'CINVOICE';
    closeClientInvoiceListModal();
  };

  const onSupplierInvoiceSelect = (selectedInvoice: SIListResponse) => {
    form.setFieldsValue({ objectid: selectedInvoice.sireference });

    searchParams.objectid = selectedInvoice.siid;
    searchParams.objectreference = selectedInvoice.sireference;
    searchParams.objecttype = 'SINVOICE';
    closeSupplierInvoiceListModal();
  };

  const onPaymentSelect = (selectedPayment: PAYListResponse) => {
    form.setFieldsValue({ objectid: selectedPayment.payreference });

    searchParams.objectid = selectedPayment.payid;
    searchParams.objectreference = selectedPayment.payreference;
    searchParams.objecttype = 'PAYMENT';
  };

  const [searchParams, setSearchParams] = useState<any>({
    objecttype: objecttype,
    objectid: objectid,
    objectreference: '',
    currcode: '',
    clear: false,
  });

  const useObjectQuery = (objectid: any, objecttype: string) => {
    const paynetQuery = useQuery(
      ['getRefPayNettingView', objectid],
      () =>
        getRefPayNettingView({ paynetref: objectid })
          .then((res) => res.list || [])
          .catch((error) => {
            return [];
          }),
      {
        refetchOnMount: false,
        enabled: objecttype === 'PAYNET' && objectid !== '' && !isNil(objectid),
      }
    );

    const ciQuery = useQuery(
      ['getCIPayNettingView', objectid],
      () =>
        getCIPayNettingView({ ciid: objectid })
          .then((res) => res.list || [])
          .catch((error) => {
            return [];
          }),
      {
        refetchOnMount: false,
        enabled:
          objecttype === 'CINVOICE' && objectid !== '' && !isNil(objectid),
      }
    );

    const siQuery = useQuery(
      ['getSIPayNettingView', objectid],
      () =>
        getSIPayNettingView({ siid: objectid })
          .then((res) => res.list || [])
          .catch((error) => {
            return [];
          }),
      {
        refetchOnMount: false,
        enabled:
          objecttype === 'SINVOICE' && objectid !== '' && !isNil(objectid),
      }
    );
    const payQuery = useQuery(
      ['getPAYPayNettingView', objectid],
      () =>
        getPAYPayNettingView({ payid: objectid })
          .then((res) => res.list || [])
          .catch((error) => {
            return [];
          }),
      {
        refetchOnMount: false,
        enabled:
          objecttype === 'PAYMENT' && objectid !== '' && !isNil(objectid),
      }
    );
    const waccQuery = useQuery(
      ['getWACCPayNettingView', objectid],
      () =>
        getWACCPayNettingView({
          tpid: objectid,
          currcode: searchParams.currcode,
        })
          .then((res) => res.list || [])
          .catch((error) => {
            return [];
          }),
      {
        refetchOnMount: false,
        enabled: objecttype === 'WACC' && objectid !== '' && !isNil(objectid),
      }
    );

    switch (objecttype) {
      case 'PAYNET':
        return paynetQuery;

      case 'CINVOICE':
        return ciQuery;

      case 'SINVOICE':
        return siQuery;
      case 'PAYMENT':
        return payQuery;
      case 'WACC':
        return waccQuery;
      default:
        return paynetQuery;
    }
  };

  const {
    data: dataSource,
    isFetching,
    refetch,
  } = useObjectQuery(
    objectid ?? searchParams.objectid,
    searchParams.objecttype
  );

  useEffect(() => {
    if (!ismodal) refetch();
  }, [refetch, refetchNetting, ismodal, objectid]);

  const columns = useMemo<DataGridColDef<PayNettingViewResponse>[]>(
    () => [
      {
        dataIndex: 'paynetreference',
        filtrable: true,
        customfilter: true,
        filterComponent: (
          <Row>
            <Col span={18}>
              <Form.Item shouldUpdate noStyle>
                <FormInput name="operation">
                  <Radio.Group
                    onChange={(e) => {
                      setSearchParams({
                        objecttype: e.target.value,
                        clear: !searchParams.clear,
                        objectid: null,
                      });
                      form.setFieldsValue({ objectid: null });
                    }}
                  >
                    <Radio value="PAYNET">Netting Reference </Radio>
                    <Radio value="CINVOICE">Client Invoice</Radio>
                    <Radio value="SINVOICE">Supplier Invoice</Radio>
                    <Radio value="PAYMENT">Payment</Radio>
                    <Radio value="WACC">Waiting Account</Radio>
                  </Radio.Group>
                </FormInput>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const objecttype = getFieldValue('operation');
                  return (
                    <FormInput name="objectid">
                      {objecttype === 'CINVOICE' ? (
                        <ClientInvoiceSelect
                          onChange={(e: any) => {
                            onClientInvoiceSelect(e);
                          }}
                          clear={searchParams.clear}
                        />
                      ) : objecttype === 'SINVOICE' ? (
                        <SupplierInvoiceSelect
                          onChange={(e: any) => {
                            onSupplierInvoiceSelect(e);
                          }}
                          clear={searchParams.clear}
                        />
                      ) : objecttype === 'PAYMENT' ? (
                        <PaymentSelect
                          onChange={(e: any) => {
                            onPaymentSelect(e);
                          }}
                          clear={searchParams.clear}
                        />
                      ) : objecttype === 'WACC' ? (
                        <ThirdPartyInput
                          onChange={(e) => {
                            setSearchParams({
                              objectid: e.tpid,
                              objecttype: 'WACC',
                            });
                          }}
                        />
                      ) : (
                        <Input
                          allowClear
                          onChange={(e) => {
                            setSearchParams({
                              objectid: e.target.value,
                              objecttype: 'PAYNET',
                            });
                          }}
                        />
                      )}
                    </FormInput>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        ),
        comcode: 'NettingTable.paynetreference',
        render: (value, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{record.paynetreference}</a>
            )}
            modal={({ setVisible, visible }) => (
              <NettingList
                hideCol
                hideExtraAction
                objectid={record.paynetreference}
                objecttype="PAYNET"
                onCancel={() => setVisible(false)}
                showAsModal
                visible={visible}
              />
            )}
          />
        ),
        fixed: 'left',

        width: '18%',
      },
      {
        dataIndex: 'operationlabel',
        comcode: 'NettingTable.operationlabel',

        width: '20%',
      },
      {
        dataIndex: 'payreference',
        comcode: 'NettingTable.payreference',
        render: (payreference, record) => (
          <>
            {record.payid && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <PayementModal
                    payid={record.payid}
                    showAsModal
                    visible={visible}
                    onCancel={() => setVisible(false)}
                    onSuccess={() => setVisible(false)}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a
                    onClick={() => {
                      setVisible(true);
                    }}
                  >
                    {record.payreference}
                  </a>
                )}
              />
            )}
            {record.ciidcreditnote && record.paynettype === 'CIC' && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <CIDetail
                    ciid={record.ciidcreditnote}
                    onCancel={() => setVisible(false)}
                    showAsModal
                    visible={visible}
                    showButtons={false}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a onClick={() => setVisible(true)}>{record.payreference}</a>
                )}
              />
            )}
          </>
        ),
        width: '15%',
      },

      {
        align: 'left',
        comcode: 'NettingTable.objectreference',
        dataIndex: 'objectreference',
        render: (objectreference, record) => (
          <>
            {objectreference && record.paynettype === 'WACC' && (
              <Text>{objectreference}</Text>
            )}
            
            {objectreference && (record.paynettype === 'DISB' || record.paynettype==='ELEMWACC') && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <ProposalModal
                    ctrid={record.ctrid}
                    visible={visible}
                    onCancel={() => setVisible(false)}
                    onSuccess={() => setVisible(false)}
                    title=""
                    phase="PROD"
                    iscreate={false}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a onClick={() => setVisible(true)}>
                    {record.objectreference}
                  </a>
                )}
              />
            )}

            {objectreference && record.paynettype === 'CIC' && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <CIDetail
                    ciid={record.ciid}
                    onCancel={() => setVisible(false)}
                    showAsModal
                    visible={visible}
                    showButtons={false}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a onClick={() => setVisible(true)}>
                    {record.objectreference}
                  </a>
                )}
              />
            )}
            {objectreference && record.paynettype === 'REIM' && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <PayementModal
                    payid={record.payidlinked}
                    showAsModal
                    visible={visible}
                    onCancel={() => setVisible(false)}
                    onSuccess={() => setVisible(false)}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a onClick={() => setVisible(true)}>
                    {record.objectreference}
                  </a>
                )}
              />
            )}
            {objectreference && record.paynettype === 'SIC' && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <SIDetail
                    siid={record.siidcreditnote}
                    onCancel={() => setVisible(false)}
                    showAsModal
                    visible={visible}
                    showButtons={false}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a onClick={() => setVisible(true)}>
                    {record.objectreference}
                  </a>
                )}
              />
            )}
            {record.ciid !== null &&
              (record.paynettype === 'CI' ||
                record.paynettype === 'CIWACC') && (
                <ModalControllerNV
                  modal={({ setVisible, visible }) => (
                    <CIDetail
                      ciid={record.ciid}
                      onCancel={() => setVisible(false)}
                      showAsModal
                      visible={visible}
                      showButtons={false}
                    />
                  )}
                  content={({ setVisible, visible }) => (
                    <a onClick={() => setVisible(true)}>
                      {record.objectreference}
                    </a>
                  )}
                />
              )}
            {record.siid !== null && record.paynettype === 'SI' && (
              <ModalControllerNV
                modal={({ setVisible, visible }) => (
                  <SIDetail
                    siid={record.siid}
                    onCancel={() => setVisible(false)}
                    showAsModal
                    visible={visible}
                    showButtons={false}
                  />
                )}
                content={({ setVisible, visible }) => (
                  <a onClick={() => setVisible(true)}>
                    {record.objectreference}
                  </a>
                )}
              />
            )}
          </>
        ),
        width: '30%',
      },
      {
        dataIndex: 'paynetdate',
        filtrable: false,
        comcode: 'NettingTable.paynetdate',
        render: (val, record) => val.substring(0, 10),
        width: '10%',
      },
      {
        dataIndex: 'currcode',
        filtrable: true,
        filterComponent: (
          <ParListSelect
            style={{ width: '100%' }}
            onSelect={(e) => {
              onChangeCurrency(e);
            }}
            parcode="CURRENCY"
          />
        ),
        comcode: 'NettingTable.currcode',

        width: '10%',
      },
      {
        align: 'left',
        comcode: 'NettingTable.debit',
        dataIndex: 'debit',
        render: (val, record) => (
          <Typography.Text
            strong={record.paynetstatus === 'VALID'}
            style={{
              color: ['VALID', 'INIT'].includes(record.paynetstatus)
                ? '#389e0d'
                : record.paynetstatus === 'CANCEL'
                ? 'rgba(212,107,8,0.7)'
                : 'rgba(207,19,34,0.7)',
            }}
          >
            {toCurrency(val, record.currcode)}
          </Typography.Text>
        ),
        width: '15%',
      },
      {
        align: 'left',
        comcode: 'NettingTable.credit',
        dataIndex: 'credit',
        render: (val, record) => (
          <Typography.Text
            strong={record.paynetstatus === 'VALID'}
            style={{
              color: ['VALID', 'INIT'].includes(record.paynetstatus)
                ? '#389e0d'
                : record.paynetstatus === 'CANCEL'
                ? 'rgba(212,107,8,0.7)'
                : 'rgba(207,19,34,0.7)',
            }}
          >
            {toCurrency(val, record.currcode)}
          </Typography.Text>
        ),
        width: '15%',
      },
      {
        align: 'left',
        comcode: 'NettingTable.statuslabel',
        dataIndex: 'statuslabel',
        render: (val, record, i) => {
          let color;
          if (record.paynetstatus === 'INIT') {
            color = 'green';
          }
          if (record.paynetstatus === 'VALID') {
            color = 'green';
          }

          if (record.paynetstatus === 'CANCEL') {
            color = 'orange';
          }

          if (record.paynetstatus === 'REJECT') {
            color = 'red';
          }

          return (
            <Tag
              icon={
                record.paynetstatus === 'VALID' ? <CheckCircleOutlined /> : null
              }
              color={color}
              key={record.paynetid}
            >
              {val}
            </Tag>
          );
        },

        width: '10%',
      },

      // {
      //   comcode: 'Action',
      //   key: 'operation',
      //   fixed: 'right',
      //   width: '10%',
      //   render: (record) => (
      //     <TasksListPopover
      //       entityId={record.payid}
      //       entityName="PAYMENT"
      //       onSuccess={refetch}
      //     >
      //       <AntdButton icon={<ArrowsAltOutlined />} size="small">
      //         Actions
      //       </AntdButton>
      //     </TasksListPopover>
      //   ),
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      form,
      openClientInvoiceListModal,
      openSupplierInvoiceListModal,
      refetch,
      searchParams,
    ]
  );

  const search = () => {
    if (!isNil(searchParams.objectid)) {
      setSearchParams({ ...searchParams });
      refetch();
    }
  };

  return (
    <DF.Provider pagcode="paynetlist">
      <Page
        modalTitle="Netting list"
        showAsModal={showAsModal}
        visible={visible}
        onCancel={onCancel}
      >
        <Form form={form} layout="vertical">
          <DataGrid
            rowKey="paynetid"
            dataSource={dataSource}
            titleComcode=""
            columns={
              !hideCol
                ? columns
                : columns.filter((ea) => ea.key !== 'operation')
            }
            onClear={() => {
              form.setFieldsValue({ objectid: null, objecttype: null });
              setSearchParams({
                ...searchParams,
                clear: !searchParams.clear,
                objectid: null,
                objecttype: null,
              });
            }}
            onSearch={search}
            initialSearchValues={searchParams}
            isLoading={isFetching}
            spanFilter={[24, 4]}
            pagination={{ responsive: true }}
            hideExtraAction={hideExtraAction}
            hideFilters={hideFilters}
            extraActions={
              <NewButton to="/paynetting/create" comcode="header.btnnew" />
            }
          />
        </Form>
      </Page>
    </DF.Provider>
  );
};

export default NettingList;
